import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, AlertTriangle, FlaskConical, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { getEntry, LIBRARY, pubmedUrl } from "@/data/researchLibrary";
import { trackEvent } from "@/lib/track";
import { useCatalog } from "@/context/CatalogContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVIDENCE_COLORS = {
  "FOUNDATIONAL": "text-emerald-400 border-emerald-400/40",
  "EARLY TRANSLATIONAL": "text-sky-400 border-sky-400/40",
  "PRECLINICAL": "text-[#D4AF37] border-[#D4AF37]/40",
  "LIMITED / EMERGING": "text-amber-400 border-amber-400/40",
  "POORLY CHARACTERIZED": "text-zinc-400 border-zinc-600",
};

function Section({ title, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{title}</p>
      {children}
    </div>
  );
}

export default function ResearchArticle() {
  const { slug } = useParams();
  const { getProduct } = useCatalog();
  const [e, setE] = useState(() => getEntry(slug));
  const [loading, setLoading] = useState(!getEntry(slug));
  const [requested, setRequested] = useState(() => localStorage.getItem(`mx_req_${slug}`) === "1");

  const requestCompound = async () => {
    try {
      const email = localStorage.getItem("mx_email") || null;
      const { data } = await axios.post(`${API}/compound-requests`, { slug, email });
      trackEvent("inquiry_submitted", { kind: "compound_request", slug });
      setRequested(true);
      localStorage.setItem(`mx_req_${slug}`, "1");
      toast.success(`Request recorded — ${data.requests} researcher${data.requests === 1 ? " has" : "s have"} asked for this compound`);
    } catch { toast.error("Could not record your request — please try again"); }
  };

  useEffect(() => {
    let alive = true;
    setE(getEntry(slug));
    setLoading(!getEntry(slug));
    trackEvent("research_content_view", { slug });
    axios.get(`${API}/research/${slug}`).then(({ data }) => {
      if (alive && data) { setE(data); setLoading(false); }
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const product = useMemo(() => (e ? getProduct(e.productId) : null), [e, getProduct]);
  const specRows = useMemo(() => [
    ["Classification", e?.classification],
    ["Sequence", e?.sequence],
    ["Formula", e?.formula],
    ["Molecular weight", e?.mw],
    ["CAS", e?.cas],
    ["PubChem", e?.pubchem],
    ["Catalog / SKU", product ? `${product.name} · ${product.sku} · ${product.size}` : null],
    ["Targets", (e?.targets && e.targets.length) ? e.targets.join("; ") : null],
  ].filter(([, v]) => v), [e, product]);

  if (loading) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="library-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#52525B] animate-pulse">Loading research entry…</p>
      </main>
    );
  }

  if (!e) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="library-not-found">
        <p className="font-serif text-4xl font-light mb-6">Research entry not found.</p>
        <Link to="/library" className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4AF37]">← Back to Research Library</Link>
      </main>
    );
  }

  const related = LIBRARY.filter((x) => x.slug !== e.slug && (x.areas || []).some((a) => (e.areas || []).includes(a))).slice(0, 3);

  return (
    <main className="pt-28 pb-24" data-testid="research-article-page">
      <Seo title={`Research Library | ${e.name}`} path={`/library/${e.slug}`}
        description={`${e.name}: ${e.classification} ${(e.overview || "").slice(0, 120)} Research use only.`} />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/library" data-testid="article-back"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors mb-10">
          <ArrowLeft size={15} /> Research Library
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-4">
            <FlaskConical size={18} className="text-[#D4AF37]" />
            <span className={`font-mono text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 border ${EVIDENCE_COLORS[e.evidence] || "text-zinc-400 border-zinc-600"}`} data-testid="article-evidence">{e.evidence}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-3" data-testid="article-name">{e.name}</h1>
          {e.aliases && e.aliases.length > 0 && (
            <p className="font-mono text-[12px] text-[#71717A] mb-4">Also known as: {e.aliases.join(", ")}</p>
          )}
          <div className="flex flex-wrap gap-2 mb-8">
            {(e.areas || []).map((a) => (
              <span key={a} className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] border border-zinc-800 px-2 py-1">{a}</span>
            ))}
          </div>
        </motion.div>

        {e.verify && (
          <div className="flex items-start gap-3 border border-amber-400/40 bg-amber-400/[0.05] p-5 mb-8" data-testid="article-verify">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[#D4C08A] text-sm leading-relaxed">{e.verifyNote || "Scientific identity requires verification. Independent, authoritative characterization of this material is limited — consult Molecularix product documentation for product-specific identity and specification information."}</p>
          </div>
        )}

        <div className="border border-zinc-800 divide-y divide-zinc-800 mb-10">
          {specRows.map(([label, value]) => (
            <div key={label} className="flex items-start gap-4 p-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] w-28 shrink-0">{label}</span>
              <span className="text-[#F5F5F0] text-sm flex-1 break-words">{value}</span>
            </div>
          ))}
        </div>

        {e.blend && e.blend.length > 0 && (
          <Section title="Blend Composition" testid="article-blend">
            <div className="border border-zinc-800 divide-y divide-zinc-800">
              {e.blend.map((c) => (
                <div key={c.name} className="flex items-center justify-between gap-4 p-4">
                  <span className="text-[#F5F5F0] text-sm">
                    {c.slug ? <Link to={`/library/${c.slug}`} className="text-[#D4AF37] hover:underline">{c.name}</Link> : c.name}
                  </span>
                  <span className="font-mono text-[12px] text-[#A1A1AA]">{c.amount}</span>
                </div>
              ))}
            </div>
            <p className="text-[#71717A] text-[13px] leading-relaxed mt-3">A blend is not a single molecular entity — no single formula, molecular weight, or CAS applies. Product-level verification requires an exact-lot analytical record.</p>
          </Section>
        )}

        <Section title="Molecular Overview" testid="article-overview"><p className="text-[#A1A1AA] text-[15px] leading-relaxed">{e.overview}</p></Section>
        {e.mechanism && <Section title="Mechanistic Background"><p className="text-[#A1A1AA] text-[15px] leading-relaxed">{e.mechanism}</p></Section>}
        {e.studies && <Section title="What Researchers Study"><p className="text-[#A1A1AA] text-[15px] leading-relaxed">{e.studies}</p></Section>}
        {e.combination && (
          <Section title="Combination Research Material">
            <p className="text-[#A1A1AA] text-[15px] leading-relaxed">This is a combination material. Evidence for individual components does not establish the behaviour or efficacy of the specific combination.
              {e.components && e.components.length > 0 && (
                <> See component overviews: {e.components.map((c, i) => {
                  const ce = LIBRARY.find((x) => x.slug === c);
                  return ce ? <React.Fragment key={c}>{i > 0 ? ", " : " "}<Link to={`/library/${c}`} className="text-[#D4AF37] hover:underline">{ce.name}</Link></React.Fragment> : null;
                })}.</>
              )}
            </p>
          </Section>
        )}
        <Section title="Evidence Landscape" testid="article-evidence-section">
          <p className="text-[#A1A1AA] text-[15px] leading-relaxed">Current evidence level: <span className="text-[#F5F5F0]">{e.evidence}</span>. Preclinical evidence does not equate to demonstrated human efficacy.</p>
        </Section>
        {e.limitations && (
          <Section title="Research Limitations"><p className="text-[#A1A1AA] text-[15px] leading-relaxed">{e.limitations}</p></Section>
        )}

        <Section title="Scientific References" testid="article-references">
          {e.refs && e.refs.length > 0 ? (
            <ul className="space-y-2">
              {e.refs.map((r, i) => (
                <li key={r.term || r.text || i} className="text-sm">
                  <a href={pubmedUrl(r.term)} target="_blank" rel="noreferrer" data-testid={`article-ref-${i}`}
                    className="inline-flex items-start gap-1.5 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
                    <ExternalLink size={13} className="mt-0.5 shrink-0 text-[#D4AF37]" />
                    <span>{r.text || `Search PubMed: "${r.term}"`}</span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[#71717A] text-sm">Curated citations pending verification. Consult PubMed and Molecularix product documentation.</p>
          )}
        </Section>

        {/* CTAs */}
        <div className="flex flex-wrap gap-3 mt-10 pt-8 border-t border-zinc-900">
          {product && !product.soldOut && (
            <Link to={`/product/${e.productId}`} data-testid="article-view-product"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
              View product <ArrowRight size={13} />
            </Link>
          )}
          {!product && (
            <button onClick={requestCompound} disabled={requested} data-testid="request-compound-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <PackagePlus size={14} /> {requested ? "Request Recorded ✓" : "Request This Compound"}
            </button>
          )}
          <Link to="/documentation" className="inline-flex items-center gap-2 border border-zinc-700 text-[#A1A1AA] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Documentation</Link>
        </div>

        {/* RUO */}
        <div className="mt-10 border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-6" data-testid="article-ruo">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Research Use Only</p>
          <p className="text-[#71717A] text-sm leading-relaxed">Molecularix Biolabs provides research materials for legitimate laboratory research. Information in this Research Library is provided for educational and scientific reference purposes and is not medical advice, treatment guidance, or a recommendation for human or veterinary use.</p>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Related Research</p>
            <div className="grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
              {related.map((r) => (
                <Link key={r.slug} to={`/library/${r.slug}`} data-testid={`related-research-${r.slug}`}
                  className="bg-[#0A0A0A] p-5 hover:bg-[#0F0F0F] transition-colors">
                  <p className="font-serif text-lg font-light hover:text-[#D4AF37] transition-colors">{r.name}</p>
                  <p className="font-mono text-[10px] text-[#71717A] mt-1">{(r.areas || [])[0]}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
